import {WidgetConfig} from "types/config";
import {Alpine} from "@alpinejs/csp";
import {OutputMessageObject} from "types/chat";
import {FeedbackRating, FeedbackReason, FeedbackState, Salutation} from "types/enums";
import {
    FEEDBACK_RATING_SUBMIT_EVENT,
    FEEDBACK_REASON_SUBMIT_EVENT,
    IRatingEventDetails,
    IReasonEventDetails
} from "types/events";
import XDataContext = Alpine.XDataContext;
import AlpineComponent = Alpine.AlpineComponent;
import AlpineMagics = Alpine.Magics;

type FeedbackComponentFactory = (
    widgetConfig: WidgetConfig,
    message: OutputMessageObject) => AlpineComponent<IFeedbackComponent>;

interface IReasonOption {
    code: FeedbackReason,
    label: string
}
export interface IFeedbackComponent {
    readonly confirmationText: string;
    readonly maxFeedbackFreeTextLength: number;
    readonly reasonOptions: IReasonOption[];
    readonly feedbackSubmitConfirmationText: string;
    feedbackState: FeedbackState;
    isRatingLocked: boolean;
    isHidden: boolean;
    isSubmitEnabled: boolean;
    get showReasonPanel(): boolean;
    selectedReasonCode: FeedbackReason | null;
    freeText: string;
    freeTextCharCount: number;
    handleThumbUp(): void,
    handleThumbDown(): void,
    onReasonChange(): void,
    updateCharCount(): void,
    submitFeedbackReason(): void,
}

export interface IHistoryMessageState {
    isInteractive: boolean;
    initialRating?: FeedbackRating | null;
    initialReasonGiven?: boolean;
}

class FeedbackComponent implements IFeedbackComponent, XDataContext
{
    readonly confirmationText: string;
    readonly maxFeedbackFreeTextLength: number;
    readonly reasonOptions: IReasonOption[];
    readonly trademark: string;
    readonly feedbackSubmitConfirmationText: string;
    readonly salutation: Salutation;
    feedbackState: FeedbackState = FeedbackState.NONE;
    isRatingLocked: boolean = false;
    isHidden: boolean = false;
    isSubmitEnabled: boolean = false;
    selectedReasonCode: FeedbackReason | null = null;
    freeText: string = '';
    freeTextCharCount: number = 0;

    private doShowReasonPanel: boolean = false;

    constructor(
        widgetConfig: WidgetConfig,
        private readonly message: OutputMessageObject)
    {
        this.confirmationText = widgetConfig.getFeedbackConfirmationText();
        this.maxFeedbackFreeTextLength = widgetConfig.getMaxFeedbackFreeTextLength();
        this.reasonOptions = this.initializeReasonOptions(widgetConfig);
        this.trademark = widgetConfig.trademark;
        this.feedbackSubmitConfirmationText = widgetConfig.feedbackSubmitConfirmationText;
        this.salutation = widgetConfig.salutation;
    }

    private initializeReasonOptions(widgetConfig: WidgetConfig): IReasonOption[]
    {
        return [
            FeedbackReason.NOT_UNDERSTOOD,
            FeedbackReason.UNCLEAR,
            FeedbackReason.NOT_HELPFUL,
            FeedbackReason.OTHER
        ].map((reason): IReasonOption => {
            return { code: reason, label: widgetConfig.getLabelForReasonCode(reason) };
        });
    }

    init(): void {
        this.feedbackComponent.$watch('showReasonPanel', async (newVal: boolean) => {
            if (newVal) {
                await this.scrollFeedbackContainerToEnd();
            }
        });

        this.feedbackComponent.$watch('feedbackState', async (newVal: FeedbackState) => {
            if (newVal === FeedbackState.COMPLETED || newVal === FeedbackState.THUMB_UP) {
                await this.scrollFeedbackContainerToEnd();
            }
        });

        this.initFromHistory();
    }

    private async scrollFeedbackContainerToEnd() {
        await this.feedbackComponent.$nextTick();

        this.feedbackComponent.$refs.feedbackScrollContainer.scrollIntoView(
            {
                behavior: 'smooth',
                block: 'end'
            }
        );
    }

    private get buttonUpCssClass(): string {
        switch (true) {
            case this.feedbackState === FeedbackState.THUMB_UP:
                return "active filled";
            default:
                return "outline";
        }
    }

    private get buttonDownCssClass(): string {
        const isFilled = this.feedbackState === FeedbackState.THUMB_DOWN
            || this.feedbackState === FeedbackState.COMPLETED;

        switch (true) {
            case isFilled:
                return "active filled";
            default:
                return "outline";
        }
    }

    get showReasonPanel(): boolean {
        return this.doShowReasonPanel && (this.feedbackState === FeedbackState.THUMB_DOWN || this.feedbackState === FeedbackState.COMPLETED);
    }

    private set showReasonPanel(value: boolean) {
        this.doShowReasonPanel = value;
    }

    private initFromHistory(): void {
        const historyMessageState = this.message.historyMessageState ?? null;

        if (null === historyMessageState) {
            return;
        }

        switch (true) {
            case !historyMessageState.isInteractive:
                this.isHidden = true;
                break;
            case null === historyMessageState.initialRating:
                this.feedbackState = FeedbackState.NONE;
                break;
            case FeedbackRating.THUMB_UP === historyMessageState.initialRating:
                this.feedbackState = FeedbackState.THUMB_UP;
                this.isRatingLocked = true;
                break;
            case FeedbackRating.THUMB_DOWN === historyMessageState.initialRating && historyMessageState.initialReasonGiven:
                this.feedbackState = FeedbackState.COMPLETED;
                this.isRatingLocked = true;
                break;
            default:
                this.feedbackState = FeedbackState.THUMB_DOWN;
                this.isRatingLocked = true;
                this.showReasonPanel = true;
        }
    }

    hideFeedback(): void {
        this.isHidden = true;
        this.isRatingLocked = true;
    }

    handleThumbUp(): void {
        if (this.isRatingLocked) {
            return;
        }

        this.feedbackState = FeedbackState.THUMB_UP;
        this.isRatingLocked = true;

        const ratingEventDetails: IRatingEventDetails = {
            id: this.message.responseId,
            rating: FeedbackRating.THUMB_UP,
            messageId: this.message.messageId,
        };

        this.feedbackComponent.$dispatch(FEEDBACK_RATING_SUBMIT_EVENT, ratingEventDetails);
    }

    async handleThumbDown(): Promise<void> {
        if (this.isRatingLocked) {
            return;
        }

        this.feedbackState = FeedbackState.THUMB_DOWN;
        this.showReasonPanel = true;
        this.isRatingLocked = true;

        const ratingEventDetails: IRatingEventDetails = {
            id: this.message.responseId,
            rating: FeedbackRating.THUMB_DOWN,
            messageId: this.message.messageId,
        };

        this.feedbackComponent.$dispatch(FEEDBACK_RATING_SUBMIT_EVENT, ratingEventDetails);
    }

    async onReasonChange(): Promise<void> {
        this.isSubmitEnabled = null !== this.selectedReasonCode;

        if (this.selectedReasonCode === FeedbackReason.OTHER) {
            await this.scrollFeedbackContainerToEnd();
        }
    }

    submitFeedbackReason(): void {
        if (null === this.selectedReasonCode) {
            return;
        }

        if (this.feedbackState !== FeedbackState.THUMB_DOWN) {
            return;
        }

        this.feedbackState = FeedbackState.COMPLETED;
        this.isSubmitEnabled = false;

        const reasonEventDetails: IReasonEventDetails = {
            id: this.message.responseId,
            reason: this.selectedReasonCode,
            freeText: this.freeText,
            messageId: this.message.messageId,
        };

        this.feedbackComponent.$dispatch(FEEDBACK_REASON_SUBMIT_EVENT, reasonEventDetails);
    }

    updateCharCount(): void {
        this.freeTextCharCount = this.freeText.trim().length;
    }

    private get feedbackButtonText(): string {
        return this.showReasonPanel && this.feedbackState === FeedbackState.COMPLETED ?
            this.feedbackSubmitConfirmationText :
            'Feedback absenden';
    }

    private get feedbackComponent(): AlpineMagics<this>
    {
        return this as unknown as AlpineMagics<this>;
    }

    private get freeTextPlaceholder(): string {
        if (this.salutation === Salutation.DU) {
            return 'Bitte beschreibe Dein Anliegen...';
        }

        return 'Bitte beschreiben Sie Ihr Anliegen...';
    }

    private get freeTextTitle(): string {
        if (this.salutation === Salutation.DU) {
            return 'Hier kannst Du Dein Feedback eintragen';
        }

        return 'Hier können Sie Ihr Feedback eintragen';
    }
}

const feedbackComponentFactory: FeedbackComponentFactory = (
    widgetConfig: WidgetConfig,
    message: OutputMessageObject) => {
    return new FeedbackComponent(widgetConfig, message);
}

export default feedbackComponentFactory;